/**
 * useAutoReport Hook
 *
 * Escuta eventos `api:server-error` do apiClient e envia reports
 * ao backend para criação automática de issues no Hub.
 * Inclui debounce e deduplicação local.
 *
 * @since 2026-03-26
 * @issue TH-49421
 */

import { useEffect, useRef } from 'react';
import i18next from 'i18next';
import { useAuthStore } from '@/store';
import { getBackendUrl, getApiPrefix } from '@/config/backend';

interface ServerErrorDetail {
  status: number;
  url: string;
  method: string;
  message: string;
}

interface ErrorReportPayload {
  status?: number;
  url?: string;
  method?: string;
  message: string;
  stack?: string;
  component?: string;
  page_url?: string;
  user_agent?: string;
}

// Cache local para deduplicação (evita enviar o mesmo erro repetidamente)
const reportedErrors = new Set<string>();
const DEDUP_TTL = 60 * 60 * 1000; // 1 hora

function errorHash(detail: ServerErrorDetail): string {
  return `${detail.status}:${detail.url}:${detail.method}:${detail.message.slice(0, 200)}`;
}

async function sendReport(payload: ErrorReportPayload): Promise<void> {
  try {
    const token = useAuthStore.getState().token;

    const baseUrl = `${getBackendUrl()}${getApiPrefix()}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const resp = await fetch(`${baseUrl}/core/system/error-report`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    if (!resp.ok) {
      console.warn('[AutoReport] Backend retornou', resp.status);
    }
  } catch {
    // Silencioso — não queremos loops de erro
  }
}

/**
 * Hook que ativa o auto-report de erros.
 * Deve ser chamado uma vez no componente raiz (App).
 */
export function useAutoReport(): void {
  const listenerRef = useRef(false);

  useEffect(() => {
    // Evitar registrar listener duplicado
    if (listenerRef.current) return;
    listenerRef.current = true;

    const handleServerError = (event: Event) => {
      const detail = (event as CustomEvent<ServerErrorDetail>).detail;
      if (!detail?.message) return;

      const hash = errorHash(detail);
      if (reportedErrors.has(hash)) return;

      reportedErrors.add(hash);
      // Limpar hash após TTL
      setTimeout(() => reportedErrors.delete(hash), DEDUP_TTL);

      sendReport({
        status: detail.status,
        url: detail.url,
        method: detail.method,
        message: detail.message,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      });
    };

    // Capturar erros JS não tratados (React render errors, etc)
    const handleUnhandledError = (event: ErrorEvent) => {
      const message = event.message || 'Unhandled error';
      const hash = `unhandled:${message.slice(0, 200)}`;
      if (reportedErrors.has(hash)) return;

      reportedErrors.add(hash);
      setTimeout(() => reportedErrors.delete(hash), DEDUP_TTL);

      sendReport({
        message: `[JS Error] ${message}`,
        stack: event.error?.stack,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      });
    };

    // Capturar Promise rejections não tratadas
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message = event.reason?.message || String(event.reason) || 'Unhandled rejection';
      const hash = `rejection:${message.slice(0, 200)}`;
      if (reportedErrors.has(hash)) return;

      reportedErrors.add(hash);
      setTimeout(() => reportedErrors.delete(hash), DEDUP_TTL);

      sendReport({
        message: `[Promise Rejection] ${message}`,
        stack: event.reason?.stack,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      });
    };

    // Capturar missing i18n keys
    const handleMissingKey = (lngs: readonly string[], namespace: string, key: string) => {
      const message = `[i18n] missingKey: ${namespace}:${key} (${lngs.join(',')})`;
      const hash = `i18n:${namespace}:${key}`;
      if (reportedErrors.has(hash)) return;

      reportedErrors.add(hash);
      setTimeout(() => reportedErrors.delete(hash), DEDUP_TTL);

      sendReport({
        message,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      });
    };

    window.addEventListener('api:server-error', handleServerError);
    window.addEventListener('error', handleUnhandledError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    i18next.on('missingKey', handleMissingKey);

    return () => {
      window.removeEventListener('api:server-error', handleServerError);
      window.removeEventListener('error', handleUnhandledError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      i18next.off('missingKey', handleMissingKey);
      listenerRef.current = false;
    };
  }, []);
}
